import { Button, Paper, Stack, Typography, SxProps } from '@mui/material';
import { useIsDesktop, useDictionary } from 'Hooks';
import { sendCookieConsentEvent } from 'Utils/sendEvent';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { FC, useState, useEffect } from 'react';

const shouldShowCookiePopup = async (): Promise<boolean> => {
  return false;
  // TODO: Uncomment this when we have a way to check if the user is in the EU
  // try {
  //   const res = await fetch('https://ipapi.co/json/');
  //   const data: { country_code?: string } = await res.json();

  //   return !!data?.country_code && EU_COUNTRIES.includes(data.country_code);
  // } catch {
  //   return false;
  // }
};

interface ICookieConsentPopupProps {
  sx?: SxProps;
}

export const CookieConsentPopup: FC<ICookieConsentPopupProps> = ({ sx }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.CookieConsent);

  useEffect(() => {
    let timer: number | undefined;
    let isMounted = true;

    const initPopup = async () => {
      // Check if user has already consented
      const hasConsented = localStorage.getItem('cookie-consent');
      if (hasConsented) {
        return;
      }

      const isEuropeanUser = await shouldShowCookiePopup();

      if (isMounted && isEuropeanUser) {
        // Show popup after a short delay to ensure smooth page load
        timer = window.setTimeout(() => {
          setIsVisible(true);
          setIsAnimating(true);
        }, 1000);
      }
    };

    initPopup();

    return () => {
      isMounted = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const handleGotIt = () => {
    // Track event with Microsoft Clarity using project utility
    sendCookieConsentEvent();

    // Store consent in localStorage
    localStorage.setItem('cookie-consent', 'true');

    // Hide popup with animation
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  const handlePrivacyPolicy = () => {
    // Open privacy policy in new tab
    window.open('https://shikum.mod.gov.il/about/privacy', '_blank');
  };

  if (!isVisible) {
    return null;
  }

  const desktopStyles: SxProps = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '378px',
    height: '159px',
    opacity: isAnimating ? 1 : 0,
    transform: isAnimating ? 'translateY(0)' : 'translateY(20px)',
    transition: 'all 0.3s ease-in-out',
    zIndex: 1000,
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    ...sx,
  };

  const mobileStyles: SxProps = {
    position: 'fixed',
    bottom: '39px',
    right: '24px',
    left: '24px',
    height: '160px',
    maxWidth: 'none',
    borderRadius: '12px',
    opacity: isAnimating ? 1 : 0,
    transform: isAnimating ? 'translateY(0)' : 'translateY(20px)',
    transition: 'all 0.3s ease-in-out',
    zIndex: 1000,
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    ...sx,
  };

  return (
    <Paper sx={isDesktop ? desktopStyles : mobileStyles} elevation={3}>
      <Stack
        spacing={2}
        sx={{ p: 2.5, height: '100%', justifyContent: 'space-between' }}
      >
        <Stack>
          <Typography
            variant='h6'
            sx={{
              fontFamily: 'Assistant',
              fontWeight: '700 !important',
              fontStyle: 'Bold',
              fontSize: '18px !important',
              lineHeight: '24px',
              letterSpacing: '0px',
              color: 'text.primary',
            }}
          >
            {dictionary.title}
          </Typography>

          <Typography
            variant='body2'
            sx={{
              fontFamily: 'Assistant',
              fontWeight: '400 !important',
              fontStyle: 'Regular',
              fontSize: '18px !important',
              lineHeight: '24px',
              letterSpacing: '0px',
              color: 'text.secondary',
            }}
          >
            {dictionary.body}
          </Typography>
        </Stack>
        <Stack
          direction='row'
          spacing={2}
          sx={{
            gap: '16px',
          }}
        >
          <Button
            variant='outlined'
            onClick={handlePrivacyPolicy}
            sx={{
              width: '165px',
              height: '37px',
              fontSize: '14px',
              fontWeight: 500,
              borderColor: 'grey.300',
              color: 'text.primary',
              backgroundColor: '#ffffff',
              fontFamily: 'inherit',
              '&:hover': {
                borderColor: 'grey.400',
                backgroundColor: 'grey.50',
              },
            }}
          >
            {dictionary.privacyPolicy}
          </Button>

          <Button
            variant='contained'
            onClick={handleGotIt}
            sx={{
              width: '165px',
              height: '37px',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: 'inherit',
            }}
          >
            {dictionary.gotIt}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default CookieConsentPopup;
